/**
 * Every sound the board makes, synthesized with Web Audio: no files to load.
 * Browsers only allow audio after a key or tap, so unlock() runs from the dial keypress.
 */

const DTMF: Record<string, [number, number]> = {
  "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
  "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
  "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
  "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
};

const PREF_KEY = "atdt.sound";
const wait = (s: number) => new Promise((r) => setTimeout(r, s * 1000));

export class Sound {
  private ctx?: AudioContext;
  private out?: GainNode;
  /** The caller's own switch, remembered in this browser. */
  on: boolean;
  /** The board's say: which sounds exist at all, and how loud. */
  modem = true;
  chimes = true;
  private volume = 0.3;

  constructor() {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(PREF_KEY);
    } catch {
      // Storage blocked: default to on.
    }
    this.on = saved !== "off";
  }

  configure(skin: { modem: boolean; chime: boolean; volume: number } | undefined) {
    this.modem = skin?.modem ?? false;
    this.chimes = skin?.chime ?? false;
    this.volume = Math.min(Math.max(skin?.volume ?? 0.3, 0), 1);
    if (this.out) this.out.gain.value = this.volume;
  }

  toggle(): boolean {
    this.on = !this.on;
    try {
      localStorage.setItem(PREF_KEY, this.on ? "on" : "off");
    } catch {
      // Not remembered; still applies now.
    }
    return this.on;
  }

  unlock() {
    if (this.ctx) return void this.ctx.resume();
    try {
      this.ctx = new AudioContext();
      this.out = this.ctx.createGain();
      this.out.gain.value = this.volume;
      this.out.connect(this.ctx.destination);
    } catch {
      // No Web Audio: stay silent.
    }
  }

  private get live(): boolean {
    return this.on && !!this.ctx && !!this.out;
  }

  /** Sine tones at `freqs`, starting `at` seconds from now, with soft edges so they don't click. */
  private tone(freqs: number[], at: number, dur: number, level = 0.5) {
    const ctx = this.ctx!;
    const t0 = ctx.currentTime + at;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(level / freqs.length, t0 + 0.008);
    env.gain.setValueAtTime(level / freqs.length, t0 + dur - 0.012);
    env.gain.linearRampToValueAtTime(0, t0 + dur);
    env.connect(this.out!);
    for (const f of freqs) {
      const osc = ctx.createOscillator();
      osc.frequency.value = f;
      osc.connect(env);
      osc.start(t0);
      osc.stop(t0 + dur + 0.02);
    }
  }

  /** Band-limited noise, for the part of the handshake that sounds like static. */
  private hiss(at: number, dur: number, center: number, level: number) {
    const ctx = this.ctx!;
    const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = center;
    band.Q.value = 0.9;
    const env = ctx.createGain();
    const t0 = ctx.currentTime + at;
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(level, t0 + 0.05);
    // The data carrier "breathes" as the modems train.
    for (let t = 0.1; t < dur - 0.1; t += 0.18) env.gain.setValueAtTime(level * (0.55 + Math.random() * 0.45), t0 + t);
    env.gain.linearRampToValueAtTime(0, t0 + dur);
    src.connect(band).connect(env).connect(this.out!);
    src.start(t0);
    src.stop(t0 + dur);
  }

  /** Dial tone, then the number as DTMF. Resolves when the last digit ends. */
  async dial(number: string) {
    if (!this.live || !this.modem) return wait(0.6);
    this.tone([350, 440], 0, 0.7, 0.35);
    let at = 0.8;
    for (const ch of number) {
      const pair = DTMF[ch];
      if (!pair) continue;
      this.tone(pair, at, 0.09, 0.45);
      at += 0.15;
    }
    await wait(at);
  }

  async ring() {
    if (!this.live || !this.modem) return wait(0.7);
    this.tone([440, 480], 0.2, 1.1, 0.35);
    await wait(1.6);
  }

  /** The answering modem's 2100 Hz tone, an FSK warble, then scrambled data. */
  async handshake() {
    if (!this.live || !this.modem) return wait(0.3);
    this.tone([2100], 0, 0.8, 0.3);
    let at = 0.9;
    for (let i = 0; i < 10; i++, at += 0.04) this.tone([i % 2 ? 1180 : 980], at, 0.04, 0.3);
    this.tone([1650, 1850], at + 0.05, 0.25, 0.25);
    this.hiss(at + 0.35, 1.3, 1800, 0.5);
    this.hiss(at + 0.35, 1.3, 600, 0.25);
    await wait(at + 1.7);
  }

  /** Three bursts of the North American busy signal. */
  async busy() {
    if (!this.live || !this.modem) return;
    for (let i = 0; i < 3; i++) this.tone([480, 620], i, 0.5, 0.35);
    await wait(3);
  }

  /** Someone walked into the lounge. */
  chime() {
    if (!this.live || !this.chimes) return;
    this.tone([880], 0, 0.14, 0.25);
    this.tone([1320], 0.12, 0.22, 0.2);
  }

  /** A private message for you. */
  blip() {
    if (!this.live || !this.chimes) return;
    this.tone([1568], 0, 0.07, 0.25);
    this.tone([1568], 0.1, 0.07, 0.25);
  }
}
